import { useRef, useCallback } from 'react';
import { isValid } from 'date-fns';
import type { FormikHelpers } from 'formik';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { dateTimeToTimeString } from '@/common/application/utils/timeUtils';
import { useUpdateRaptorJob } from '@/raptorjob/application/hooks/useRaptorJob';
import { mapERPJobToUpdateInput } from '@/raptorjob/application/adapters/mapERPJobToUpdateInput';
import {
  useCreateJobOptionalFieldsPreferences,
  type CreateJobOptionalFieldsValues
} from '@/raptorjob/presentation/hooks/useCreateJobOptionalFieldsPreferences';
import { useERPJobMutations } from './useERPJobMutations';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';

interface UseERPJobFormActionsParams {
  mode: JobFormMode;
  jobId?: number;
  handleClose: () => void;
  openCreatedJob: (id: string) => void;
  onSave?: (jobId: string) => void;
}

export const useERPJobFormActions = ({ mode, jobId, handleClose, openCreatedJob, onSave }: UseERPJobFormActionsParams) => {
  const { savedValues, setSavedValues } = useCreateJobOptionalFieldsPreferences();
  const latestFormValuesRef = useRef<ERPJobFormValues | null>(null);

  const { handleSubmit: handleCreateSubmit } = useERPJobMutations({ openCreatedJob });
  const { mutateAsync: updateRaptorJob } = useUpdateRaptorJob();

  const buildOptionalFieldsValues = useCallback(
    (values: ERPJobFormValues): CreateJobOptionalFieldsValues => ({
      ...savedValues,
      vendorId: values.vendorId ?? null,
      showUpTime:
        values.showUpTime && typeof values.showUpTime === 'object' && isValid(values.showUpTime)
          ? (dateTimeToTimeString(values.showUpTime) ?? null)
          : null,
      leaveByTime:
        values.leaveByTime && typeof values.leaveByTime === 'object' && isValid(values.leaveByTime)
          ? (dateTimeToTimeString(values.leaveByTime) ?? null)
          : null,
      onJobTime:
        values.onJobTime && typeof values.onJobTime === 'object' && isValid(values.onJobTime)
          ? (dateTimeToTimeString(values.onJobTime) ?? null)
          : null,
      poNumber: values.poNumber ?? null
    }),
    [savedValues]
  );

  const handleSave = async (values: ERPJobFormValues, helpers: FormikHelpers<ERPJobFormValues>) => {
    if (mode === JOB_FORM_MODES.EDIT) {
      if (!jobId) {
        console.error('Job ID is required for edit mode');
        return;
      }

      try {
        helpers.setSubmitting(true);
        const updateInput = mapERPJobToUpdateInput(values);
        await updateRaptorJob({
          id: jobId,
          data: updateInput
        });

        if (onSave) {
          onSave(jobId.toString());
        }

        handleClose();
      } catch (error) {
        console.error('Failed to update raptor job:', error);
      } finally {
        helpers.setSubmitting(false);
      }
    } else {
      await handleCreateSubmit(values, helpers);
      setSavedValues(buildOptionalFieldsValues(values));
    }
  };

  const trackFormValues = useCallback((values: ERPJobFormValues) => {
    latestFormValuesRef.current = values;
  }, []);

  const handleCloseWithPersist = useCallback(() => {
    if (mode === JOB_FORM_MODES.CREATE && latestFormValuesRef.current) {
      setSavedValues(buildOptionalFieldsValues(latestFormValuesRef.current));
    }
    handleClose();
  }, [mode, handleClose, setSavedValues, buildOptionalFieldsValues]);

  return {
    handleSave,
    handleCloseWithPersist,
    trackFormValues
  };
};
